<template>
  <div class="h-full" ref="orderPanel">
    <!-- Loading state -->
    <div v-if="isLoading" class="h-full flex items-center justify-center">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p class="text-gray-600">{{ $t('ui.loading') }}</p>
      </div>
    </div>

    <!-- Always render the current page component -->
    <component :is="currentComponent" v-bind="currentProps" :embedded="true" v-on="componentEventListeners"
      v-else-if="currentComponent" :key="componentKey" />

    <!-- Default Home View when no component is loaded -->
    <div v-else class="h-full">
      <div class="p-4 border-b">
        <h3 class="text-lg font-medium text-gray-900">{{ $t('ui.order_management') }}</h3>
      </div>
      <div class="p-6">
        <div class="flex gap-6 flex-wrap lg:flex-row-reverse lg:justify-end">
          <template v-for="module in links">
            <div v-if="module.active !== false" :key="module.id" @click="handleLinkClick(module)"
              class="bg-white dark:bg-gray-900 shadow-md rounded-lg p-6 h-40 flex flex-col items-center justify-start text-center cursor-pointer hover:shadow-lg transition w-full sm:w-40">
              <v-icon :name="module.icon" class="mb-3 mt-2" scale="1.5" />
              <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ $t('modules.' + module.slug) }}</p>
            </div>
            <div
                v-else
                class="bg-white dark:bg-gray-900 shadow-md rounded-lg p-6 w-40 h-40 flex flex-col items-center justify-start text-center cursor-pointer hover:shadow-lg transition
                      w-full sm:w-40"
            >
                <v-icon :name="module.icon" class="mb-3 mt-2 text-gray-400" scale="1.5" />
                <p class="text-lg font-semibold text-gray-400 dark:text-gray-100">{{ $t('modules.' + module.slug) }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { router, usePage } from '@inertiajs/vue3';
import { markRaw } from 'vue';
import axios from 'axios';
import Lista from '@/Pages/Modules/gestione-ordini/Lista.vue';
import Ordine from '@/Pages/Modules/gestione-ordini/Ordine.vue';

export default {
  name: 'OrderPanel',
  emits: ['reload-mails'],
  components: {
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    submodules: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentComponent: null,
      currentProps: {},
      isInterceptingNavigation: true,
      componentKey: 0,
      isLoading: false,
      originalUiPolicies: null,
      abortController: null,
      navigationHandler: null,
      inertiaBeforeHandler: null
    };
  },
  computed: {
    links() {
      let entries = [];

      this.submodules.forEach(submodule => {
        entries.push({
          id: submodule.id,
          name: this.$t(submodule.name),
          slug: submodule.slug,
          icon: submodule.icon,
          href: route(submodule.route),
          active: submodule.active
        });
      });

      return entries;
    },
    componentEventListeners() {
      const baseListeners = {
        'go-back-to-home': this.goHome,
        'reload-mails': this.reloadEmails
      };

      if (this.currentComponent?.__name === 'MailRenderer') {
        baseListeners['create-order-from-email'] = this.createOrderFromEmail;
      }

      return baseListeners;
    }
  },
  methods: {

    reloadEmails(){
      this.$emit('reload-mails');
    },

    handleLinkClick(link) {
      if (link.id === 'lista-ordini') {
        this.navigateToRoute('modules.ordini.lista-ordini');
      } else if (link.id === 'lista-offerte') {
        this.navigateToRoute('modules.ordini.lista-offerte');
      }
    },

    navigateToRoute(routeName, params = {}) {
      this.interceptNavigation(routeName, params);
    },

    interceptNavigation(routeName, params = {}) {
      const routeMap = {
        'modules.ordini.lista-ordini': {
          method: 'post',
          url: route('modules.ordini.lista-ordini'),
          component: Lista,
          data: {}
        },
        'modules.ordini.lista-offerte': {
          method: 'post',
          url: route('modules.ordini.lista-offerte'),
          component: Lista,
          data: {}
        },
        'modules.ordini.documento': {
          method: 'get',
          url: route('modules.ordini.documento', { id: params.id || -1 }) + this.buildQueryString(params),
          component: Ordine,
          data: {}
        }
      };

      const config = routeMap[routeName];
      if (!config) {
        return;
      }

      if (this.originalUiPolicies === null) {
        try {
          this.originalUiPolicies = JSON.parse(JSON.stringify(usePage().props.ui_policies || {}));
        } catch (e) {
          console.warn('Failed to clone ui_policies:', e);
          this.originalUiPolicies = {};
        }
      }

      const isOrdineComponent = routeName === 'modules.ordini.documento';
      const currentDocumentId = this.currentProps?.head?.id;
      const newDocumentId = params.id || -1;

      if (isOrdineComponent && this.currentComponent && (currentDocumentId !== newDocumentId)) {
        this.componentKey++;
      }

      this.isLoading = true;

      this.abortController = new AbortController();

      const axiosConfig = {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'X-Inertia': 'true',
          'X-Inertia-Version': usePage().version ?? ''
        },
        signal: this.abortController.signal
      };

      const requestPromise = config.method === 'post'
        ? axios.post(config.url, config.data, axiosConfig)
        : axios.get(config.url, axiosConfig);

      requestPromise
        .then(response => {
          return Promise.resolve(config.component).then(resolvedComponent => {
            const decodedData = response.data;

            const globalProps = [
              'errors', 'jetstream', 'auth', 'errorBags', 'CustSuppTypes',
              'user', 'isSuperAdmin', 'isAdmin', 'isAdminOrSuperAdmin',
              'roles', 'modules', 'customer', 'config', 'locale'
            ];

            const filteredProps = Object.keys(decodedData.props)
              .filter(key => !globalProps.includes(key))
              .reduce((obj, key) => {
                obj[key] = decodedData.props[key];
                return obj;
              }, {});

            if (filteredProps.ui_policies) {
              Object.assign(usePage().props.ui_policies, filteredProps.ui_policies);
            }

            this.currentComponent = markRaw(resolvedComponent.default || resolvedComponent);
            this.currentProps = filteredProps;
            this.isLoading = false;
          });
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            return;
          }
          console.error('Navigation error:', error);
          this.isLoading = false;
        });
    },

    goHome() {
      if (this.abortController) {
        this.abortController.abort();
      }
      if (this.originalUiPolicies !== null) {
        Object.assign(usePage().props.ui_policies, this.originalUiPolicies);
        this.originalUiPolicies = null;
      }
      this.currentComponent = null;
      this.currentProps = {};
      this.isLoading = false;
      this.componentKey++;
    },


    setupNavigationInterception() {
      this.navigationHandler = (e) => {
        const link = e.target.closest('a[href]');

        if (!link || !this.$refs.orderPanel || !this.$refs.orderPanel.contains(link)) {
          return;
        }

        const href = link.getAttribute('href');

        const shouldIntercept = href && (
          href.includes('/go/ordini') ||
          href.includes('/go/offerte') ||
          href.includes('/go/documento/') ||
          href.includes('/go/configuratore')
        );

        if (shouldIntercept) {
          e.preventDefault();
          e.stopPropagation();

          if (href.includes('/go/ordini')) {
            this.navigateToRoute('modules.ordini.lista-ordini');
          } else if (href.includes('/go/offerte')) {
            this.navigateToRoute('modules.ordini.lista-offerte');
          } else if (href.includes('/go/documento/')) {
            const match = href.match(/\/go\/documento\/(\d+)/);
            const id = match ? match[1] : -1;

            const url = new URL(href, window.location.origin);
            const type = url.searchParams.get('type');
            const customerEmail = url.searchParams.get('customerEmail');

            this.navigateToRoute('modules.ordini.documento', { id, type, customerEmail });
          } else if (href.includes('/go/configuratore')) {
            this.navigateToRoute('modules.ordini.configuratore');
          }
        }
      };

      document.addEventListener('click', this.navigationHandler, true);
    },

    cleanupNavigationInterception() {
      if (this.navigationHandler) {
        document.removeEventListener('click', this.navigationHandler, true);
      }
    },

    setupInertiaInterception() {
      this.inertiaBeforeHandler = (event) => {
        const url = String(event.detail?.visit?.url || '');

        if (url.includes('/go/')) {
          if (url.includes('deeptable') || url.includes('articletable') || url.includes('servicetable')) {
            return;
          }

          if (url.includes('/go/ordini') || url.includes('/go/offerte') || url.includes('/go/documento') || url.includes('/go/configuratore')) {
            event.preventDefault();
          }
        }
      };

      document.addEventListener('inertia:before', this.inertiaBeforeHandler);
    },

    cleanupInertiaInterception() {
      if (this.inertiaBeforeHandler) {
        document.removeEventListener('inertia:before', this.inertiaBeforeHandler);
      }
    },

    createOrderFromEmail(data) {
      this.isLoading = true;

      if (data.extradata.weborders_head_id) {
        this.navigateToRoute('modules.ordini.documento', {
          id: data.extradata.weborders_head_id,
        });
        return;
      }
      else {
        this.navigateToRoute('modules.ordini.documento', {
          id: -1,
          type: 'order',
          customerEmail: data.senderEmail,
          priority: data.extradata.priority,
          mailId: data.extradata.id
        });
      }
    },

    buildQueryString(params) {
      const queryParams = new URLSearchParams();

      if (params.type) {
        queryParams.append('type', params.type);
      }

      if (params.customerEmail) {
        queryParams.append('customerEmail', params.customerEmail);
      }

      if (params.priority) {
        queryParams.append('priority', params.priority);
      }

      if (params.mailId) {
        queryParams.append('mailId', params.mailId);
      }

      const queryString = queryParams.toString();
      return queryString ? `?${queryString}` : '';
    }
  },

  mounted() {
    this.setupNavigationInterception();
    this.setupInertiaInterception();
  },

  beforeUnmount() {
    this.cleanupNavigationInterception();
    this.cleanupInertiaInterception();
    if (this.abortController) {
      this.abortController.abort();
    }
  }
};
</script>

<style scoped>
.h-full {
  height: 100%;
}
</style>
